/**
 * Meta (Facebook) Pixel + CAPI helper.
 *
 * - Fires browser Pixel events AND sends matching server-side CAPI events.
 * - Both share the same event_id for deduplication.
 * - Gated behind marketing consent (localStorage "marketingConsent" === "true").
 * - If META_PIXEL_ID is empty the Pixel script is never injected.
 * - Includes enhanced match quality: client_user_agent, fbc, fbp cookies.
 */

import { API } from "@/App";

// ── consent ──────────────────────────────────────────────────────────
export function hasMarketingConsent() {
  return localStorage.getItem("marketingConsent") === "true";
}

// ── helpers ──────────────────────────────────────────────────────────
function generateEventId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? match[2] : null;
}

// ── initialise Pixel script (called once, gated by consent) ──────────
let pixelLoaded = false;

export function initMetaPixel(pixelId) {
  if (!pixelId || pixelLoaded || !hasMarketingConsent()) return;

  /* eslint-disable */
  !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
  n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
  document,'script','https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */

  window.fbq("init", pixelId);
  pixelLoaded = true;
}

// ── send event to CAPI backend ───────────────────────────────────────
async function sendCAPI(eventName, eventId, extras = {}) {
  // Only send if marketing consent is given
  if (!hasMarketingConsent()) return;

  try {
    await fetch(`${API}/track/meta`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        event_name: eventName,
        event_id: eventId,
        event_time: Math.floor(Date.now() / 1000),
        page_url: window.location.href,
        // Enhanced match quality data (only sent when consented)
        client_user_agent: navigator.userAgent,
        fbc: getCookie("_fbc") || null,
        fbp: getCookie("_fbp") || null,
        ...extras,
      }),
      credentials: "omit",
    });
  } catch {
    // drop silently — tracking should never break UX
  }
}

// ── public API ───────────────────────────────────────────────────────

/**
 * Fire a standard Meta event via both Pixel and CAPI.
 * No-ops if marketing consent is not given.
 */
export function trackMetaEvent(eventName, params = {}, extras = {}) {
  if (!hasMarketingConsent()) return;

  const eventId = generateEventId();

  // Browser Pixel
  if (window.fbq) {
    window.fbq("track", eventName, params, { eventID: eventId });
  }

  // Server CAPI
  sendCAPI(eventName, eventId, extras);
}

/** PageView — call on route changes */
export function trackMetaPageView() {
  trackMetaEvent("PageView");
}

/** ViewContent — e.g. blog post view */
export function trackMetaViewContent(contentName) {
  trackMetaEvent("ViewContent", { content_name: contentName }, { content_name: contentName });
}

/** Lead — e.g. contact form submit */
export function trackMetaLead(extraData = {}) {
  trackMetaEvent("Lead", {}, extraData);
}

/** CompleteRegistration — e.g. after signup */
export function trackMetaRegistration(extraData = {}) {
  trackMetaEvent("CompleteRegistration", {}, extraData);
}
